import { ComponentProps, ReactNode } from 'react';

import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import { CloseIcon } from '@/assets/icons';
import { IconButton, Tag } from '@/components/atoms';
import { cn } from '@/utils/tailwind';

type AlertDialogProps = ComponentProps<typeof AlertDialogPrimitive.Root>;
type AlertDialogTriggerProps = ComponentProps<typeof AlertDialogPrimitive.Trigger>;
type AlertDialogPortalProps = ComponentProps<typeof AlertDialogPrimitive.Portal>;
type AlertDialogOverlayProps = ComponentProps<typeof AlertDialogPrimitive.Overlay>;
type AlertDialogContentProps = ComponentProps<typeof AlertDialogPrimitive.Content>;
type AlertDialogHeaderProps = ComponentProps<'div'>;
type AlertDialogFooterProps = ComponentProps<'div'>;
type AlertDialogTitleProps = ComponentProps<typeof AlertDialogPrimitive.Title>;
type AlertDialogDescriptionProps = ComponentProps<typeof AlertDialogPrimitive.Description>;
type AlertDialogActionProps = ComponentProps<typeof AlertDialogPrimitive.Action>;
type AlertDialogCancelProps = ComponentProps<typeof AlertDialogPrimitive.Cancel>;

type AlertDialogSimpleProps = {
  title: string;
  description: string;
  tag?: string;
  icon: ReactNode;
  actionButton: ReactNode;
  cancelButton: ReactNode;
};

const AlertDialog = ({ ...props }: AlertDialogProps) => {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
};

const AlertDialogTrigger = ({ ...props }: AlertDialogTriggerProps) => {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
};

const AlertDialogPortal = ({ ...props }: AlertDialogPortalProps) => {
  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
};

const AlertDialogOverlay = ({ className, ...props }: AlertDialogOverlayProps) => {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-[#57534E4D] backdrop-blur-md',
        className
      )}
      {...props}
    />
  );
};

const AlertDialogContent = ({ className, ...props }: AlertDialogContentProps) => {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        className={cn(
          'bg-custom-warm-grey-25 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border border-custom-warm-grey-200 p-6 shadow-custom-alert duration-200 sm:max-w-lg',
          className
        )}
        {...props}
      />
    </AlertDialogPortal>
  );
};

const AlertDialogHeader = ({ className, ...props }: AlertDialogHeaderProps) => {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
};

const AlertDialogFooter = ({ className, ...props }: AlertDialogFooterProps) => {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
};

const AlertDialogTitle = ({ className, ...props }: AlertDialogTitleProps) => {
  return (
    <AlertDialogPrimitive.Title data-slot="alert-dialog-title" className={className} {...props} />
  );
};

const AlertDialogDescription = ({ className, ...props }: AlertDialogDescriptionProps) => {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
};

const AlertDialogAction = ({ ...props }: AlertDialogActionProps) => {
  return <AlertDialogPrimitive.Action {...props} />;
};

const AlertDialogCancel = ({ ...props }: AlertDialogCancelProps) => {
  return <AlertDialogPrimitive.Cancel {...props} />;
};

const AlertDialogSimple = ({
  title,
  description,
  actionButton,
  cancelButton,
  icon,
  tag,
}: AlertDialogSimpleProps) => {
  return (
    <AlertDialogContent>
      <div className="relative flex gap-4">
        <div>{icon}</div>
        <div>
          <div className="flex flex-col gap-2 mb-6">
            <AlertDialogTitle>
              <p className="text-body-16-semibold text-custom-warm-grey-800">{title}</p>
            </AlertDialogTitle>
            {tag && (
              <Tag
                size="x-large"
                color="grey"
                className="w-fit outline-custom-warm-grey-200 bg-custom-warm-grey-50"
                type="outlined"
                label={tag}
              />
            )}
            <p className="text-body-14-regular text-custom-warm-grey-700">{description}</p>
          </div>
          <div className="flex items-center gap-3">
            <AlertDialogAction asChild>{actionButton}</AlertDialogAction>
            <AlertDialogCancel asChild>{cancelButton}</AlertDialogCancel>
          </div>
        </div>

        <AlertDialogCancel className="absolute -top-[10px] -right-[10px]" asChild>
          <IconButton size="sm" variant="teritary" icon={<CloseIcon />} />
        </AlertDialogCancel>
      </div>
    </AlertDialogContent>
  );
};

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogSimple,
  AlertDialogTitle,
  AlertDialogTrigger,
};
